import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import NgpTextField from './NgpTextField';
import { AUTO_COMPLETE_DEFAULT, TEXT_FIELD_TYPE_TEXT } from '../../utilities/FormUtilities';
import { useDataService } from '../../services/ApiDataService';
import { isSystemDistributed, validateCollectorScope } from '../pages-admin/infrastructure/services/CollectorsService';
import { validateNotMultiHomed } from '../pages-admin/infrastructure/services/DeviceService';
import { useDebounceFn } from '../../utilities/Hooks';

const IP_ADDRESS_REGEX =
    /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
const IPV6_ADDRESS_REGEX =
    /^(?:(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){6})(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:::(?:(?:(?:[0-9a-fA-F]{1,4})):){5})(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})))?::(?:(?:(?:[0-9a-fA-F]{1,4})):){4})(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){0,1}(?:(?:[0-9a-fA-F]{1,4})))?::(?:(?:(?:[0-9a-fA-F]{1,4})):){3})(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){0,2}(?:(?:[0-9a-fA-F]{1,4})))?::(?:(?:(?:[0-9a-fA-F]{1,4})):){2})(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){0,3}(?:(?:[0-9a-fA-F]{1,4})))?::(?:(?:[0-9a-fA-F]{1,4})):)(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){0,4}(?:(?:[0-9a-fA-F]{1,4})))?::)(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){0,5}(?:(?:[0-9a-fA-F]{1,4})))?::)(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){0,6}(?:(?:[0-9a-fA-F]{1,4})))?::))))$/;

const invalidIpText = i18n._(t`Value must be a valid IP address`);
const ipNotInScopeText = i18n._(t`This IP address does not fall under any existing Collector definitions`);

/**
 * Generate multi-homed error message with primary IP
 * @param {string} primaryIp - The primary IP address
 * @returns {string} Formatted error message
 */
function getMultiHomedErrorText(primaryIp) {
    return i18n._(t`This device already belongs to multi-homed device: ${primaryIp}`);
}

/**
 * Validates IP address format (IPv4 or IPv6)
 * @param {string} value - The value to validate
 * @returns {boolean|string} True if valid, error message if invalid
 */
function validateIpFormat(value) {
    try {
        if (!_.isEmpty(value) && !IP_ADDRESS_REGEX.test(value) && !IPV6_ADDRESS_REGEX.test(value)) {
            return invalidIpText;
        }
    } catch (e) {
        return invalidIpText;
    }
    return true;
}

/**
 * Check if a string is a valid IP address format
 * @param {string} value - The value to check
 * @returns {boolean} True if valid IP format
 */
function isValidIpAddress(value) {
    return IP_ADDRESS_REGEX.test(value) || IPV6_ADDRESS_REGEX.test(value);
}

/**
 * NgpIpAddressField component
 * IP address field with format validation and optional collector scope validation.
 * All async validations are debounced by 500ms to match portal/ui behavior.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.validateCollectorScope - Enable collector scope validation (default: true)
 * @param {boolean} props.validateMultiHomed - Enable multi-homed device validation (default: true)
 * @param {boolean} props.isEditMode - Skip multi-homed validation in edit mode (default: false)
 * @param {boolean} props.required - Field is required (default: false)
 * @param {string} props.fieldName - Form field name
 * @param {Object} props.inputProps - Additional input props
 * @returns {JSX.Element}
 */
export default function NgpIpAddressField(props) {
    const {
        inputProps,
        required = false,
        fieldName,
        validateCollectorScope: enableCollectorValidation = true,
        validateMultiHomed: enableMultiHomedValidation = true,
        isEditMode = false,
        ...rest
    } = props;

    const dataService = useDataService();
    const [isDistributed, setIsDistributed] = useState(false);
    const isDistributedRef = useRef(isDistributed);

    // Cache validation results to prevent redundant API calls for unchanged values
    const lastCollectorValidationRef = useRef(null);
    const lastMultiHomedValidationRef = useRef(null);

    // Check if system is distributed on mount
    useEffect(() => {
        if (enableCollectorValidation) {
            isSystemDistributed(dataService)
                .then((distributed) => {
                    setIsDistributed(distributed);
                    isDistributedRef.current = distributed;
                })
                .catch(() => {
                    setIsDistributed(false);
                    isDistributedRef.current = false;
                });
        }
    }, [dataService, enableCollectorValidation]);

    // Debounced collector scope validation (500ms to match portal/ui)
    // Using useDebounceFn because we need to pass parameters (value, resolve)
    const debouncedCollectorValidation = useDebounceFn(
        useCallback(
            async (value, resolve) => {
                // Safety check: ensure value is not empty before API call
                if (_.isEmpty(value) || !isValidIpAddress(value)) {
                    resolve(true);
                    return;
                }

                try {
                    const isValid = await validateCollectorScope(dataService, value);
                    const result = isValid || ipNotInScopeText;
                    lastCollectorValidationRef.current = { value, result };
                    resolve(result);
                } catch (error) {
                    const result = ipNotInScopeText;
                    lastCollectorValidationRef.current = { value, result };
                    resolve(result);
                }
            },
            [dataService]
        ),
        500
    );

    // Debounced multi-homed validation (500ms to match portal/ui)
    // Using useDebounceFn because we need to pass parameters (value, resolve)
    const debouncedMultiHomedValidation = useDebounceFn(
        useCallback(
            async (value, resolve) => {
                // Safety check: ensure value is not empty before API call
                if (_.isEmpty(value) || !isValidIpAddress(value)) {
                    resolve(true);
                    return;
                }

                try {
                    const result = await validateNotMultiHomed(dataService, value);
                    const validationResult = result.isValid ? true : getMultiHomedErrorText(result.primaryIp);
                    lastMultiHomedValidationRef.current = { value, result: validationResult };
                    resolve(validationResult);
                } catch (error) {
                    // On error, allow validation to pass (don't block user)
                    lastMultiHomedValidationRef.current = { value, result: true };
                    resolve(true);
                }
            },
            [dataService]
        ),
        500
    );

    // Async validator for collector scope - runs for all systems (matches portal/ui)
    const validateCollectorScopeAsync = useCallback(
        async (value) => {
            // Only run collector validation if:
            // 1. Feature is enabled
            // 2. Value is a valid IP address format
            if (!enableCollectorValidation || _.isEmpty(value) || !isValidIpAddress(value)) {
                return true;
            }

            // If value hasn't changed and we have a cached result, return it immediately
            if (lastCollectorValidationRef.current && lastCollectorValidationRef.current.value === value) {
                return lastCollectorValidationRef.current.result;
            }

            // Trigger debounced validation with parameters
            return new Promise((resolve) => {
                debouncedCollectorValidation(value, resolve);
            });
        },
        [enableCollectorValidation, debouncedCollectorValidation]
    );

    // Async validator for multi-homed device - only runs for new devices
    const validateMultiHomedAsync = useCallback(
        async (value) => {
            // Only run multi-homed validation if:
            // 1. Feature is enabled
            // 2. Not in edit mode (skip for existing devices)
            // 3. Value is a valid IP address format
            if (!enableMultiHomedValidation || isEditMode || _.isEmpty(value) || !isValidIpAddress(value)) {
                return true;
            }

            // If value hasn't changed and we have a cached result, return it immediately
            if (lastMultiHomedValidationRef.current && lastMultiHomedValidationRef.current.value === value) {
                return lastMultiHomedValidationRef.current.result;
            }

            // Trigger debounced validation with parameters
            return new Promise((resolve) => {
                debouncedMultiHomedValidation(value, resolve);
            });
        },
        [enableMultiHomedValidation, isEditMode, debouncedMultiHomedValidation]
    );

    const ipAddressRules = {
        validate: {
            ipFormat: (value) => validateIpFormat(value),
            collectorScope: validateCollectorScopeAsync,
            multiHomed: validateMultiHomedAsync,
        },
    };

    return (
        <NgpTextField
            required={required}
            fieldType={TEXT_FIELD_TYPE_TEXT}
            inputProps={{ ...inputProps, autoComplete: AUTO_COMPLETE_DEFAULT }}
            fieldRules={ipAddressRules}
            fieldName={fieldName}
            {...rest}
        />
    );
}
